import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useForm, UseFormValues } from "../../../@vendor/utils/hooks/use-form";
import { Assessment, AssessmentSchedule } from "../../../models/Assessment";
import { Psychologist } from "../../../models/Psychologist";
import { Patient } from "../../../models/Patient";
import { Schedule, ScheduleTime } from "../../../models/Schedule";
import { useLocation } from "react-router-dom";

export type BookingAssessmentForm = {
  assessment_id: number | null;
  assessment: Assessment | null;
  psychologist_id: number | null;
  psychologist: Psychologist | null;
  patient_id: number | null;
  patient: Patient | null;
  type: "Online" | "Offline" | null;
  schedule_ids: number[];
  schedule: Schedule[];
  time_ids: number[];
  time: ScheduleTime[];
  assessmentSchedules: AssessmentSchedule[];
  continueAsGuest?: boolean;
};

type BookingAssessmentContextType = {
  currentStep: number;
  onPrevStep: () => void;
  onNextStep: () => void;
  setStep: (step: number) => void;
  form: UseFormValues<BookingAssessmentForm>;
  triggerValidationError: boolean;
  setTriggerValidationError: (value: boolean) => void;
  manageStepValidator: (
    step: number,
    fn?: () => string | null
  ) => (() => void) | string | null;
  clearContext: () => void;
};

const BookingAssessmentContext = createContext<
  BookingAssessmentContextType | undefined
>(undefined);

export const BookingAssessmentProvider = (props: PropsWithChildren) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [triggerValidationError, setTriggerValidationError] = useState<boolean>(false);
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  const onPrevStep = useCallback(() => {
    if (currentStep <= 0) return;
    setCurrentStep((prev) => prev - 1);
  }, [currentStep]);

  const onNextStep = useCallback(() => {
    if (currentStep >= 2) return;
    setCurrentStep((prev) => prev + 1);
  }, [currentStep]);
  const form = useForm<BookingAssessmentForm>({
    assessment_id: null,
    assessment: null,
    psychologist_id: null,
    psychologist: null,
    patient_id: null,
    patient: null,
    type: null,
    schedule_ids: [],
    schedule: [],
    time_ids: [],
    time: [],
    assessmentSchedules: [],
    continueAsGuest: false,
  });

  const setStep = useCallback((step: number) => {
    setCurrentStep(step);
  }, []);

  const validatorsRef = React.useRef<Map<number, () => string | null>>(new Map());

  const manageStepValidator = useCallback((step: number, fn?: () => string | null) => {
    if (typeof fn === "function") {
      validatorsRef.current.set(step, fn);
      return () => validatorsRef.current.delete(step);
    }

    const fnRun = validatorsRef.current.get(step);
    if (fnRun) return fnRun();
    return null;
  }, []);

  const clearContext = useCallback(() => {
    setCurrentStep(0);
    form.resetForm();
    validatorsRef.current.clear();
  }, [form]);

  useEffect(() => {
    const prevPath = prevPathRef.current;
    const currentPath = location.pathname;

    const isLeavingBookingPage =
      prevPath.startsWith("/booking-assessment") && !currentPath.startsWith("/booking-assessment");
    const isRedirectToLogin = currentPath.startsWith("/auth/login");

    if (isLeavingBookingPage && !isRedirectToLogin) {
      clearContext();
    }

    prevPathRef.current = currentPath;
  }, [location.pathname, clearContext]);


  return (
    <BookingAssessmentContext.Provider
      value={{
        currentStep,
        onPrevStep,
        onNextStep,
        setStep,
        form,
        triggerValidationError,
        setTriggerValidationError,
        manageStepValidator,
        clearContext,
      }}
    >
      {props.children}
    </BookingAssessmentContext.Provider>
  );
};

export const useBookingAssessment = () => {
  const ctx = useContext(BookingAssessmentContext);
  if (!ctx) {
    throw new Error(
      "useBookingAssessment must be used within a BookingAssessmentProvider"
    );
  }
  return ctx;
};
